import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchStorePhoto } from "../../services/storeMasterService";
import {
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogContent,
  Skeleton,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Box
} from "@mui/material";
import Cookies from "js-cookie";

function PublicPhotoStore({ storeId: propStoreId }) {
  const params = useParams();
  const accessToken = Cookies.get("access_token") || "";
  const storeId = propStoreId || params?.storeId;

  // Store & Photos States
  const [storeDetail, setStoreDetail] = useState({});
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const order = "desc";
  const [sortBy] = useState("created_at");
  const [loading, setLoading] = useState(false);

  // Project Lazy Loading States
  const [activeProject, setActiveProject] = useState(null);
  const [dataProject, setDataProject] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Asset Generation States
  const [generatingAsset, setGeneratingAsset] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ type: "", text: "" });

  // Zoom Modal
  const [open, setOpen] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);

  // --- TRIGGER ASSET GENERATION ---
  const handleGenerateAsset = useCallback(async (projectId) => {
    if (!projectId || !accessToken) return;
    setGeneratingAsset(true);
    setAlertMessage({ type: "", text: "" });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/project-assets/generate/${projectId}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const data = await response.json();

      if (response.status === 202) {
        setAlertMessage({ type: "info", text: data.message || "" });
      } else {
        setAlertMessage({ type: "error", text: data.detail || "Failed to start generation automatically." });
      }
    } catch (error) {
      console.error("Error starting asset generation:", error);
      setAlertMessage({ type: "error", text: "Network error occurred during auto-generation." });
    } finally {
      setGeneratingAsset(false);
    }
  }, [accessToken]);

  // --- FETCH PROJECTS (With Search & Pagination) ---
  const fetchProject = useCallback(async (pageNumber = 1, search = "", isAppend = false) => {
    if (!accessToken) return;
    setLoadingProjects(true);
    try {
      const pageSize = 15;
      let url = `${process.env.REACT_APP_API_URL}/api/v1/project?sort_by=created_at&sort_order=desc&page=${pageNumber}&size=${pageSize}`;

      if (search) {
        url += `&column_name=project_name&column_operator=LIKE&column_value=${encodeURIComponent(search)}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) return;

      const { data } = await response.json();

      const formattedData = data.map(item => ({
        label: item.project_name,
        id: item.survey_id,
        project_type_id: item.project_type_id,
      }));

      if (isAppend) {
        setDataProject(prev => [...prev, ...formattedData]);
      } else {
        setDataProject(formattedData);
      }

      setHasMoreProjects(data.length === pageSize);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  }, [accessToken]);

  // Bootstrap layout trigger to initialize project listing lookup on mount
  useEffect(() => {
    fetchProject(1, "", false);
  }, [fetchProject]);

  // --- FETCH PHOTOS ---
  const fetchPhotoStore = useCallback(async (pageNumber = 1, orderValue = order, surveyId = "") => {
    if (!storeId) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/public-data/store/${storeId}`);
      if (!response.ok) return;
      const data = await response.json();
      setStoreDetail(data.data);

      const photoRes = await fetchStorePhoto(accessToken, surveyId, storeId, pageNumber, limit, sortBy, orderValue);
      if (photoRes) {
        setPhotos(photoRes.items);
        setTotal(photoRes.total);
        setPage(photoRes.page);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [accessToken, storeId, limit, sortBy, order]);

  // Initial Photo Load Trigger
  useEffect(() => {
    if (storeId) {
      fetchPhotoStore(1, order, "");
    }
  }, [storeId, order, fetchPhotoStore]);

  // --- MANUAL CHECK STATUS & REFRESH ---
  const handleCheckStatusAndRefresh = async () => {
    if (!accessToken) return;
    setCheckingStatus(true);
    setAlertMessage({ type: "", text: "" });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/project-assets/status`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.status === 404) {
        setAlertMessage({ type: "info", text: "No background tasks found. Refreshing photos..." });
      } else if (response.ok) {
        const data = await response.json();
        const currentStatus = data.status?.status;

        if (currentStatus === "progress") {
          setAlertMessage({ type: "warning", text: "Asset generation is still running in the background." });
        } else if (currentStatus === "success") {
          setAlertMessage({ type: "success", text: "Asset generation completed successfully!" });
        } else if (currentStatus === "failed") {
          setAlertMessage({ type: "error", text: "The last generation task failed." });
        }
      }

      await fetchPhotoStore(1, order, activeProject?.id || "");

    } catch (error) {
      console.error("Error manual refresh execution:", error);
      setAlertMessage({ type: "error", text: "Failed to sync status updates." });
    } finally {
      setCheckingStatus(false);
    }
  };

  // --- DEBOUNCED SEARCH EFFECT ---
  useEffect(() => {
    let isMounted = true;

    const delayDebounceFn = setTimeout(() => {
      if (isMounted) {
        setProjectPage(1);
        fetchProject(1, searchQuery, false);
      }
    }, 400);

    return () => {
      isMounted = false;
      clearTimeout(delayDebounceFn);
    };
  }, [searchQuery, fetchProject]);

  // --- SCROLL EVENT HANDLER FOR DROPDOWN ---
  const handleDropdownScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      !loadingProjects &&
      hasMoreProjects
    ) {
      const nextPage = projectPage + 1;
      setProjectPage(nextPage);
      fetchProject(nextPage, searchQuery, true);
    }
  };

  const handleNext = () => {
    const next = page + 1;
    if (next <= Math.ceil(total / limit)) fetchPhotoStore(next, order, activeProject?.id || "");
  };

  const handlePrev = () => {
    const prev = page - 1;
    if (prev >= 1) fetchPhotoStore(prev, order, activeProject?.id || "");
  };

  const handleOpenImage = (img) => {
    setSelectedImg(img);
    setOpen(true);
  };

  if (!storeId) {
    return <p style={{ padding: 5 }}>No store selected</p>;
  }

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "85vh", // Prevents card from running past dialog boundaries
        p: { xs: 2, sm: 4 },
        boxShadow: "none"
      }}
      component={Paper}
    >
      <Typography variant="h5" component="h4" sx={{ fontWeight: "bold" }}>
        STORE PHOTOS
      </Typography>

      {/* Scrollable Context Box */}
      <CardContent
        sx={{
          flex: 1,
          overflowY: "auto", // Keeps scroll internal to the photo list area
          display: "flex",
          flexDirection: "column",
          p: 0,
          mt: 2,
          '&:last-child': { pb: 0 }
        }}
      >
        {/* INFO */}
        <Typography sx={{ fontSize: 14 }} color="text.secondary">
          {storeDetail?.store_id}
        </Typography>
        <Typography variant="h5" sx={{ mb: 1 }}>{storeDetail?.store_name}</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {`${storeDetail?.province_name ?? ""} ${storeDetail?.regency_name ?? ""} ${storeDetail?.district_name ?? ""} ${storeDetail?.sub_district_name ?? ""}`}
        </Typography>

        {/* FEEDBACK SYSTEM ALERTS */}
        {alertMessage.text && (
          <Alert severity={alertMessage.type || "info"} sx={{ mb: 2, maxWidth: 650 }}>
            {alertMessage.text}
          </Alert>
        )}

        {/* CONTROLS ROW */}
        {dataProject.length > 0 && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3, maxWidth: 650 }}>
            <Autocomplete
              fullWidth
              size="small"
              options={dataProject}
              value={activeProject}
              loading={loadingProjects}
              inputValue={searchQuery}
              filterOptions={(x) => x}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") setSearchQuery(newInputValue);
                else if (reason === "clear") setSearchQuery("");
              }}
              onChange={(event, newValue) => {
                setActiveProject(newValue);
                fetchPhotoStore(1, order, newValue ? newValue.id : "");
                if (newValue?.id) handleGenerateAsset(newValue.id);
              }}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              ListboxProps={{
                onScroll: handleDropdownScroll,
                style: { maxHeight: "200px" }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Project"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProjects || generatingAsset ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleCheckStatusAndRefresh}
              disabled={checkingStatus || generatingAsset || loading}
              sx={{ minWidth: 130, height: 40 }}
              startIcon={checkingStatus ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {checkingStatus ? "Refreshing..." : "Refresh"}
            </Button>
          </Stack>
        )}

        {/* LOADING SKELETON */}
        {loading && (
          <ImageList cols={3} gap={10} sx={{ width: "100%" }}>
            {Array.from(new Array(9)).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            ))}
          </ImageList>
        )}

        {/* EMPTY STATE */}
        {!loading && photos.length === 0 && <p>No photos found</p>}

        {/* GALLERY */}
        {!loading && photos.length > 0 && (
          <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, mb: 2, width: "100%" }}>
            <ImageList
              variant="standard"
              cols={4}
              gap={16}
              sx={{
                width: "100%",
                my: 0,
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr) !important",
                  sm: "repeat(3, 1fr) !important",
                  md: "repeat(4, 1fr) !important",
                }
              }}
            >
              {photos.map((item) => (
                <ImageListItem
                  onClick={() => handleOpenImage(item.file)}
                  key={item.id}
                  sx={{
                    overflow: "hidden",
                    borderRadius: "10px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    flexDirection: "column",
                    height: "100% !important"
                  }}
                >
                  <Box sx={{ position: "relative", width: "100%", pt: "100%", overflow: "hidden" }}>
                    <img
                      src={item.file}
                      alt={item.id}
                      loading="lazy"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        cursor: "zoom-in",
                        filter: "blur(6px)",
                        transition: "filter 0.3s ease",
                      }}
                      onLoad={(e) => {
                        e.currentTarget.style.filter = "blur(0px)";
                      }}
                    />
                  </Box>
                  <ImageListItemBar
                    position="below"
                    sx={{
                      padding: "8px 12px",
                      background: "#fff",
                      "& .MuiImageListItemBar-titleWrap": { p: 0 }
                    }}
                    title={
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          display: 'block',
                          whiteSpace: 'normal',
                          wordBreak: 'break-all'
                        }}
                      >
                        Uploaded: {item.createdAt || "N/A"}
                      </Typography>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}
      </CardContent>

      {/* PAGINATION PANEL */}
      {!loading && photos.length > 0 && (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{
            pt: 2,
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#fff"
          }}
        >
          <Button variant="contained" size="small" onClick={handlePrev} disabled={page === 1}>
            Prev
          </Button>
          <Typography variant="body2">
            Page {page} / {Math.ceil(total / limit)}
          </Typography>
          <Button variant="contained" size="small" onClick={handleNext} disabled={page >= Math.ceil(total / limit)}>
            Next
          </Button>
        </Stack>
      )}

      {/* IMAGE ZOOM */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          {selectedImg && <img src={selectedImg} alt="zoom" style={{ width: "100%", height: "auto", display: "block" }} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default PublicPhotoStore;
